import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Layout } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { IndustryQuoteModal } from '@/components/modals/IndustryQuoteModal';
import ScheduleCallModal from '@/components/modals/ScheduleCallModal';
import { cn } from '@/lib/utils';
import SEO from '@/components/shared/SEO';
import IndustrySolutionsExplorer from '@/components/solutions/IndustrySolutionsExplorer';
import SeoContentBlock from '@/components/shared/SeoContentBlock';
import { SOLUTIONS_SEO } from '@/content/seoContentCopy';

// Zone Defense data with enhanced content
const zones = [
  {
    id: 1,
    name: 'Zone ONE Defense',
    subtitle: 'First Line Defense',
    shortName: 'Zone 1',
    tagline: 'Stop Contaminants at the Door',
    description: 'The first line of defense starts before visitors even enter your building. Zone 1 products are engineered to aggressively scrape and remove the heaviest soil, debris, and moisture from footwear—preventing up to 80% of tracked-in contaminants from ever reaching your interior floors.',
    features: [
      'Heavy-duty scraping action',
      'All-weather performance',
      'High debris capture',
      'Drainage systems'
    ],
    products: ['Entry Grid Systems', 'Outdoor Mats', 'Scraper Systems'],
    image: '/Assets/images/01_Homepage/feature_outdoor-to-main-entrance.jpg',
    articles: [
      { title: 'Entry Systems Best Practices', href: '/resources' },
      { title: 'Choosing the Right Entry Mat', href: '/resources' }
    ]
  },
  {
    id: 2,
    name: 'Zone TWO Defense',
    subtitle: 'Vestibule & Transition',
    shortName: 'Zone 2',
    tagline: 'The Critical Second Barrier',
    description: 'Zone 2 acts as your transition area—the airlock between exterior conditions and your pristine interior. These products continue the scraping process while adding superior moisture absorption. This critical zone captures what Zone 1 missed and prepares visitors for entry.',
    features: [
      'Moisture absorption',
      'Continued scraping',
      'Transition aesthetics',
      'High durability'
    ],
    products: ['Carpet Tiles', 'Entry Mats', 'Modular Systems'],
    image: '/Assets/images/01_Homepage/feature_main-entrance-to-vestibule.jpg',
    articles: [
      { title: 'Vestibule Design Guide', href: '/resources' },
      { title: 'Moisture Management Tips', href: '/resources' }
    ]
  },
  {
    id: 3,
    name: 'Zone THREE Defense',
    subtitle: 'Interior Protection',
    shortName: 'Zone 3',
    tagline: 'Where Performance Meets Design',
    description: 'The final line of defense where function meets aesthetics. Zone 3 products are designed to capture any remaining particles while delivering the visual appeal your brand deserves. From luxury vinyl to premium carpet, these solutions enhance your space while protecting your investment.',
    features: [
      'Premium aesthetics',
      'Final particle capture',
      'Brand enhancement',
      'Long-term durability'
    ],
    products: ['Luxury Vinyl', 'Carpet Tile', 'Broadloom'],
    image: '/Assets/images/01_Homepage/feature_lobby-to-back-of-house.jpg',
    articles: [
      { title: 'Interior Flooring Trends', href: '/resources' },
      { title: 'LVT vs Carpet Tile Guide', href: '/resources' }
    ]
  }
];

const SolutionsPage = () => {
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [activeZone, setActiveZone] = useState(1);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [isCallModalOpen, setIsCallModalOpen] = useState(false);
  const location = useLocation();

  // Auto-switch zone tab when arriving via #zone-1, #zone-2, #zone-3
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    const zoneMatch = hash.match(/^zone-(\d)$/);
    if (zoneMatch) {
      const zoneNum = parseInt(zoneMatch[1]);
      setActiveZone(zoneNum);
      // Scroll to the zone-defense section after React renders the new tab
      const timer = setTimeout(() => {
        const el = document.getElementById('zone-defense');
        if (el) {
          const headerOffset = 130;
          const top = el.getBoundingClientRect().top + window.scrollY - headerOffset;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [location.hash]);

  const openQuoteModal = (industry = null) => {
    setSelectedIndustry(industry);
    setIsQuoteModalOpen(true);
  };

  return (
    <Layout>
      <SEO 
        title="Flooring Solutions by Industry"
        description="Discover tailored commercial flooring solutions for your industry. From corporate offices to retail stores, healthcare to hospitality - find the perfect flooring for your space."
        canonicalUrl="/solutions"
      />
      {/* Hero Section */}
      <section className="bg-primary-bg text-primary-foreground py-16 md:py-20">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6" data-testid="solutions-hero-title">
              Flooring Solutions for Every Industry
            </h1>
            <p className="text-xl text-primary-foreground/90">
              Comprehensive flooring solutions tailored to your industry's unique challenges.
              From high-traffic retail to groceries, we have you covered.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Industry-Specific Solutions — interactive, consultative experience */}
      <IndustrySolutionsExplorer />

      {/* CTA Band */}
      <section className="bg-secondary text-secondary-foreground py-12">
        <div className="container-portico">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-2">
                Discover Your Personalized<br />Flooring System
              </h2>
              <p className="text-secondary-foreground/80">
                Learn how our Zone Defense System protects your floors from the outside in.
              </p>
            </div>
            <div className="flex gap-4">
              <Link to="/resources">
                <Button variant="outline" className="border-secondary-foreground text-secondary-foreground hover:bg-secondary-foreground hover:text-secondary">
                  Explore Resources
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="default" className="bg-primary text-primary-foreground"
                  onClick={(e) => { e.preventDefault(); setIsCallModalOpen(true); }}
                  data-testid="schedule-call-cta-solutions"
                >
                  Schedule a Call
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Zone Defense Interactive Section */}
      <section id="zone-defense" className="section-padding bg-muted" data-testid="zone-defense-section">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Zone Defense Breakdown
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Our comprehensive three-zone system provides complete protection for your commercial floors, 
              from the moment visitors arrive to their journey through your space.
            </p>
          </motion.div>

          {/* Interactive Zone Display */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Zone Image with Hotspots */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="/Assets/images/04_Solutions/modern_grocery-store.jpg"
                  alt="Zone Defense System - Modern Grocery Store"
                  className="w-full"
                  data-testid="zone-defense-image"
                />
                {/* Overlay hotspots on the image */}
                <div className="absolute inset-0">
                  {/* Zone 1 Hotspot - Bottom-left on entrance mat */}
                  <button
                    onClick={() => setActiveZone(1)}
                    onMouseEnter={() => setActiveZone(1)}
                    className={cn(
                      "absolute left-[10%] top-[64%] w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer",
                      activeZone === 1 
                        ? "bg-zone-1 text-white scale-110 shadow-lg" 
                        : "bg-white/90 text-zone-1 hover:scale-105"
                    )}
                    data-testid="zone-1-hotspot"
                  >
                    <span className="font-bold">1</span>
                  </button>
                  
                  {/* Zone 2 Hotspot - Center on glass door transition */}
                  <button
                    onClick={() => setActiveZone(2)}
                    onMouseEnter={() => setActiveZone(2)}
                    className={cn(
                      "absolute left-[34%] top-[51%] w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer",
                      activeZone === 2 
                        ? "bg-zone-2 text-white scale-110 shadow-lg" 
                        : "bg-white/90 text-zone-2 hover:scale-105"
                    )}
                    data-testid="zone-2-hotspot"
                  >
                    <span className="font-bold">2</span>
                  </button>
                  
                  {/* Zone 3 Hotspot - Upper-middle in produce area */}
                  <button
                    onClick={() => setActiveZone(3)}
                    onMouseEnter={() => setActiveZone(3)}
                    className={cn(
                      "absolute left-[50%] top-[35%] w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer",
                      activeZone === 3 
                        ? "bg-zone-3 text-white scale-110 shadow-lg" 
                        : "bg-white/90 text-zone-3 hover:scale-105"
                    )}
                    data-testid="zone-3-hotspot"
                  >
                    <span className="font-bold">3</span>
                  </button>
                </div>
              </div>

              {/* Zone Selector Buttons */}
              <div className="flex justify-center gap-4 mt-6">
                {zones.map((zone) => (
                  <button
                    key={zone.id}
                    onClick={() => setActiveZone(zone.id)}
                    onMouseEnter={() => setActiveZone(zone.id)}
                    className={cn(
                      "px-5 py-2.5 rounded-full font-semibold text-sm transition-all duration-300",
                      activeZone === zone.id
                        ? zone.id === 1 ? "bg-zone-1 text-white shadow-md" 
                          : zone.id === 2 ? "bg-zone-2 text-white shadow-md"
                          : "bg-zone-3 text-white shadow-md"
                        : "bg-card border border-border hover:border-primary"
                    )}
                    data-testid={`zone-${zone.id}-btn`}
                  >
                    {zone.shortName}
                  </button>
                ))}
              </div>
            </div>

            {/* Zone Details Panel */}
            <div className="lg:sticky lg:top-24">
              <AnimatePresence mode="wait">
                {zones.map((zone) => (
                  activeZone === zone.id && (
                    <motion.div
                      key={zone.id}
                      id={`zone-${zone.id}`}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                      data-testid={`zone-${zone.id}-details`}
                    >
                      {/* Zone Badge */}
                      <div className={cn(
                        "inline-block px-4 py-1.5 rounded-full text-sm font-semibold mb-4",
                        zone.id === 1 ? "bg-zone-1 text-white" 
                          : zone.id === 2 ? "bg-zone-2 text-white"
                          : "bg-zone-3 text-white"
                      )}>
                        {zone.subtitle}
                      </div>
                      
                      {/* Zone Title & Tagline */}
                      <h3 className="font-heading text-2xl md:text-3xl font-bold mb-2">{zone.name}</h3>
                      <p className="text-lg text-primary font-medium mb-4">{zone.tagline}</p>
                      
                      {/* Description */}
                      <p className="text-muted-foreground mb-6">{zone.description}</p>

                      {/* Features */}
                      <div className="mb-6">
                        <h4 className="font-semibold mb-3">Key Features</h4>
                        <ul className="grid grid-cols-2 gap-2">
                          {zone.features.map((feature) => (
                            <li key={feature} className="flex items-center gap-2 text-sm">
                              <CheckCircle className="w-4 h-4 text-secondary flex-shrink-0" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Recommended Products */}
                      <div className="mb-6">
                        <h4 className="font-semibold mb-3">Recommended Products</h4>
                        <div className="flex flex-wrap gap-2">
                          {zone.products.map((product) => (
                            <span key={product} className="px-3 py-1 bg-card border border-border text-sm rounded-full">
                              {product}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Related Resources */}
                      <div className="space-y-3 mb-6">
                        <h4 className="font-semibold">Related Resources</h4>
                        {zone.articles.map((article) => (
                          <Link
                            key={article.title}
                            to={article.href}
                            className="block p-4 bg-card rounded-lg border border-border hover:border-primary transition-colors group"
                          >
                            <div className="flex items-center justify-between">
                              <p className="font-medium group-hover:text-primary transition-colors">{article.title}</p>
                              <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                            </div>
                          </Link>
                        ))}
                      </div>

                      {/* CTA */}
                      <Link to={`/products?zones=${zone.id === 1 ? '1-2,1-2-3' : zone.id === 2 ? '1-2,2-3,1-2-3' : '2-3,1-2-3'}`}>
                        <Button size="lg" data-testid={`explore-zone-${zone.id}-btn`}>
                          Explore Zone {zone.id} Products
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                      </Link>
                    </motion.div>
                  )
                ))}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="section-padding bg-background">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-primary-bg text-primary-foreground rounded-2xl p-8 md:p-12 text-center"
          >
            <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
              Ready to Transform Your Space?
            </h2>
            <p className="text-primary-foreground/90 mb-8 max-w-2xl mx-auto">
              Get a customized flooring solution designed specifically for your industry and space requirements.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                variant="secondary" 
                size="lg"
                onClick={() => openQuoteModal()}
                data-testid="bottom-cta-quote-btn"
              >
                Request a Quote
              </Button>
              <Link to="/contact">
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                >
                  Talk to an Expert
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Industry Quote Modal */}
      <IndustryQuoteModal 
        isOpen={isQuoteModalOpen}
        onClose={() => setIsQuoteModalOpen(false)}
        industry={selectedIndustry}
      />

      {/* Schedule Call Modal */}
      <ScheduleCallModal isOpen={isCallModalOpen} onClose={() => setIsCallModalOpen(false)} />

      <SeoContentBlock {...SOLUTIONS_SEO} />
    </Layout>
  );
};

export default SolutionsPage;
